import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Silent session renewal: on a 401 try /auth/refresh once (14-day refresh cookie) and retry.
let refreshing = null;
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config || {};
  const url = cfg.url || "";
  if (error.response?.status !== 401 || cfg._retried || url.includes("/auth/")) throw error;
  try {
    refreshing = refreshing || axios.post(`${API}/auth/refresh`, {}, { withCredentials: true }).finally(() => { refreshing = null; });
    await refreshing;
  } catch (e) {
    throw error;
  }
  return api({ ...cfg, _retried: true });
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Trigger a browser download for a blob API response, using the filename the
// backend set in Content-Disposition (falls back to `fallbackName`).
export function saveBlob(res, fallbackName) {
  const cd = res.headers?.["content-disposition"] || "";
  const m = cd.match(/filename="?([^";]+)"?/i);
  const name = (m && m[1]) || fallbackName;
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function fmtCurrency(value, currency = "ZAR") {
  if (value == null || isNaN(value)) value = 0;
  const symbols = { ZAR: "R", EUR: "\u20ac", USD: "$" };
  const sym = symbols[currency] || `${currency} `;
  const sign = value < 0 ? "-" : "";
  const v = Math.abs(value);
  return `${sign}${sym}${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function fmtNumber(v, digits = 2) {
  if (v == null || isNaN(v)) return "0";
  return Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

// CBM per the customer's pole-taper formula:
// ((((Buttsize - LENGTH/2*7)/2)^2 * 3.14159 * LENGTH) / 1,000,000) * Quantity
export function calcCbm(butt, length, qty) {
  const b = Number(butt), L = Number(length), q = Number(qty);
  if (!isFinite(b) || !isFinite(L) || !isFinite(q) || butt == null || length == null || qty == null) return 0;
  return ((((b - (L / 2) * 7) / 2) ** 2 * 3.14159 * L) / 1000000) * q;
}

// Calculated (theoretical) weight derived from volume: CBM × 900 kg/m³.
export function calcWeight(butt, length, qty) {
  return calcCbm(butt, length, qty) * 900;
}

export function fmtDate(d) {
  if (!d) return "—";
  try {
    if (d.length > 10) {
      return new Date(d).toLocaleString();
    }
    return d;
  } catch {
    return d;
  }
}

export const ROLES = [
  { id: "admin", label: "Administrator" },
  { id: "warehouse_manager", label: "Warehouse Manager" },
  { id: "receiving_clerk", label: "Receiving Clerk" },
  { id: "dispatch_clerk", label: "Dispatch Clerk" },
  { id: "processing_team", label: "Processing Team" },
  { id: "finance_user", label: "Finance User" },
  { id: "viewer", label: "Viewer / Auditor" },
];

// Permissions check (mirrors backend logic, lite version)
const PERMS = {
  admin: ["*"],
  warehouse_manager: [
    "receiving:*", "processing:*", "stock:*", "offsite:*", "orders:*", "shipments:*", "dispatch:*",
    "damages:*", "documents:*", "master:read", "dashboard:read", "reports:read", "email:read",
    "audit:read", "finance:read", "costdash:read",
  ],
  receiving_clerk: ["receiving:*", "stock:read", "offsite:read", "damages:create", "documents:*", "master:read", "dashboard:read", "reports:read", "email:read"],
  dispatch_clerk: ["dispatch:*", "orders:read", "shipments:read", "stock:read", "offsite:read", "documents:*", "master:read", "dashboard:read", "reports:read", "email:read"],
  processing_team: ["processing:*", "stock:read", "offsite:read", "damages:create", "documents:*", "master:read", "dashboard:read", "reports:read", "email:read"],
  finance_user: ["finance:*", "costdash:*", "orders:read", "shipments:read", "dispatch:read", "stock:read", "offsite:read", "documents:*", "master:read", "dashboard:read", "reports:read", "email:read"],
  viewer: ["receiving:read", "processing:read", "stock:read", "offsite:read", "orders:read", "shipments:read", "dispatch:read", "damages:read", "finance:read", "costdash:read", "documents:read", "master:read", "dashboard:read", "reports:read", "email:read", "audit:read"],
};

const RUNTIME_PERMS = {};
// Called after login / me: server-side (admin-customised) permissions for the signed-in role.
export function setRuntimePerms(role, list) {
  if (role && Array.isArray(list)) RUNTIME_PERMS[role] = list;
}

export function can(role, perm) {
  const ps = RUNTIME_PERMS[role] || PERMS[role] || [];
  if (ps.includes("*")) return true;
  if (ps.includes(perm)) return true;
  const mod = perm.split(":")[0];
  return ps.includes(`${mod}:*`);
}

export const ORIGINS = ["EIT", "Tzaneen", "Swazi"];
export const COLOURS = ["Silver", "Yellow", "Blue", "Red", "Brown", "Green", "Orange", "Black", "Natural"];
// Butt size is auto-computed as (thick + wide) / 2 in mm — kept as a numeric value, no preset list
export const LENGTHS = [2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5, 5.9, 7.0, 7.6, 8.0];
export const PRODUCT_TYPES = ["Poles", "Lathes"];

// Diameter (butt size, mm) → paint colour. This is the fallback used only until
// the live list is fetched from /api/paint-colours (master data). Keep in sync
// with the seed in backend/routes_master.py:seed_master_data().
export const BUTT_COLOUR_MAP = [
  { butt: 90, colour: "Silver", swatch: "#c0c0c0" },
  { butt: 110, colour: "Yellow", swatch: "#facc15" },
  { butt: 130, colour: "Blue", swatch: "#1d4ed8" },
  { butt: 150, colour: "Red", swatch: "#dc2626" },
  { butt: 170, colour: "Brown", swatch: "#92400e" },
  { butt: 190, colour: "Green", swatch: "#16a34a" },
  { butt: 210, colour: "Orange", swatch: "#ea580c" },
  { butt: 230, colour: "Black", swatch: "#171717" },
];

export function colourForButt(butt, map = BUTT_COLOUR_MAP) {
  if (butt == null || butt === "" || isNaN(butt)) return null;
  if (!map || map.length === 0) return null;
  const b = Number(butt);
  return map.reduce((best, x) =>
    Math.abs(x.butt - b) < Math.abs(best.butt - b) ? x : best
  );
}

export const STATUS_LABELS = {
  received: "Received",
  pending_processing: "Pending Processing",
  processing: "Processing",
  processed: "Processed",
  awaiting_paint: "Awaiting Paint",
  available: "Available",
  allocated: "Allocated",
  dispatched: "Dispatched",
  damaged: "Damaged",
  written_off: "Written Off",
  draft: "Draft",
  open: "Open",
  partially_allocated: "Partially Allocated",
  fully_allocated: "Fully Allocated",
  ready_for_dispatch: "Ready for Dispatch",
  partially_dispatched: "Partially Dispatched",
  invoiced: "Invoiced",
  closed: "Closed",
  cancelled: "Cancelled",
  issued: "Issued",
  paid: "Paid",
  partially_paid: "Partially Paid",
  overdue: "Overdue",
};
